import React, { useState } from 'react';
import { crawlingService } from '../../services/crawlingService';
import toast from 'react-hot-toast';
import CrawlingStatusBadge from './CrawlingStatusBadge';
import ConfirmationModal from '../common/ConfirmationModal';

const RecrawlButton = ({ 
 place, 
 crawlStatus,
 onCrawlStarted = () => {}, 
 onCrawlComplete = () => {}, 
 className = '',
 size = 'md' 
}) => {
 const [loading, setLoading] = useState(false);
 const [showErrorTooltip, setShowErrorTooltip] = useState(false);
 const [confirmationModal, setConfirmationModal] = useState({
   isOpen: false,
   title: '',
   message: '',
   onConfirm: () => {},
   type: 'default'
 });

 const handleRecrawl = async () => {
   setConfirmationModal({
     isOpen: true,
     title: 'Start Re-crawl',
     message: `Start crawling "${place.display_name}" for social media information?`,
     type: 'info',
     onConfirm: async () => {
       setLoading(true);
       
       const { data, error } = await crawlingService.recrawlPlace(place.id);
       
       if (data) {
         toast.success(`Re-crawl started for ${place.display_name}`);
         onCrawlStarted(data.crawl_task);
       } else {
         const errorMessage = error?.error || 'Failed to start re-crawl';
         toast.error(errorMessage);
       }
       
       setLoading(false);
       setConfirmationModal({ ...confirmationModal, isOpen: false });
     }
   });
 };

 const getButtonContent = () => {
   if (loading) {
     return (
       <>
         <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
         Starting...
       </>
     );
   }

   const latestTask = crawlStatus?.latest_crawl_task;
   
   if (!latestTask) {
     return (
       <>
         <span className="mr-2">🕷️</span>
         Start Crawl
       </>
     );
   }

   if (['pending', 'processing', 'retrying'].includes(latestTask.status)) {
     return (
       <>
         <CrawlingStatusBadge 
           status={latestTask.status} 
           size="xs"
           showText={false}
           className="mr-2"
         />
         Crawling...
       </>
     );
   }

   return (
     <>
       <span className="mr-2">🔄</span>
       Re-crawl
     </>
   );
 };

 const getButtonStyle = () => {
   const latestTask = crawlStatus?.latest_crawl_task;
   
   if (loading || (latestTask && ['pending', 'processing', 'retrying'].includes(latestTask.status))) {
     return 'bg-gray-400 text-white cursor-not-allowed';
   }
   
   if (!latestTask) {
     return 'bg-blue-600 text-white hover:bg-blue-700';
   }
   
   if (latestTask.status === 'failed') {
     return 'bg-orange-600 text-white hover:bg-orange-700';
   }
   
   return 'bg-green-600 text-white hover:bg-green-700';
 };

 const isDisabled = () => {
   const latestTask = crawlStatus?.latest_crawl_task;
   return loading || (latestTask && ['pending', 'processing', 'retrying'].includes(latestTask.status));
 };

 const getSizeClasses = () => {
   switch (size) {
     case 'xs':
       return 'px-2 py-1 text-xs';
     case 'sm':
       return 'px-2.5 py-1.5 text-xs';
     case 'lg':
       return 'px-4 py-2 text-base';
     default:
       return 'px-3 py-1.5 text-sm';
   }
 };

 const getErrorTooltipMessage = () => {
   const latestTask = crawlStatus?.latest_crawl_task;
   const errorMsg = latestTask?.error_message?.toLowerCase() || '';
   
   if (errorMsg.includes('robots') || errorMsg.includes('not allowed') || errorMsg.includes('blocked')) {
     return 'This website has restricted automated crawling. The business owner has chosen to block automated information gathering.';
   }
   
   return 'Crawling failed due to website restrictions or technical issues. The business may have blocked automated access to their website.';
 };

 return (
   <>
     <ConfirmationModal
       isOpen={confirmationModal.isOpen}
       onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
       onConfirm={confirmationModal.onConfirm}
       title={confirmationModal.title}
       message={confirmationModal.message}
       type={confirmationModal.type}
     />
     
     <button
       onClick={handleRecrawl}
       disabled={isDisabled()}
       className={`
         ${getSizeClasses()} rounded font-medium transition-colors duration-200 select-none
         ${getButtonStyle()}
         ${className}
       `}
     >
       {getButtonContent()}
     </button>
     
     {crawlStatus?.latest_crawl_task && (
       <div className="mt-2 text-xs text-gray-500 select-none">
         {crawlStatus.latest_crawl_task.status === 'completed' && crawlStatus.total_links > 0 && (
           <span className="text-green-600">
             ✅ {crawlStatus.total_links} links found
           </span>
         )}
         
         {crawlStatus.latest_crawl_task.status === 'failed' && (
           <div className="flex items-center gap-1 text-red-600 relative select-none">
             <span>❌ Crawl failed</span>
             <div 
               className="relative inline-block"
               onMouseEnter={() => setShowErrorTooltip(true)}
               onMouseLeave={() => setShowErrorTooltip(false)}
             >
               <svg 
                 className="w-3.5 h-3.5 text-red-500 cursor-help" 
                 fill="currentColor" 
                 viewBox="0 0 20 20"
               >
                 <path 
                   fillRule="evenodd" 
                   d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
                   clipRule="evenodd" 
                 />
               </svg>
               
               {showErrorTooltip && (
                 <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-50 select-none">
                   <div className="relative">
                     {getErrorTooltipMessage()}
                     <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                       <div className="border-4 border-transparent border-t-gray-900"></div>
                     </div>
                   </div>
                 </div>
               )}
             </div>
           </div>
         )}
         
         {crawlStatus.last_crawled && (
           <div>
             Last: {new Date(crawlStatus.last_crawled).toLocaleDateString()}
           </div>
         )}
       </div>
     )}
   </>
 );
};

export default RecrawlButton;